import { Box, Button, TextField } from '@mui/material';
import { DateRangeFilter, UrlFilter, ValueOrNDash } from '@synergy/components';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { PaginatedResponse } from '@synergy/api';
import {
    PaloAmount,
    PaloColumn,
    PaloFilterChips,
    PaloFilterModal,
    PaloListView,
    usePaloListSort,
} from '@synergy/components-palo';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { SHOP_URL } from '@synergy/config';
import { UserRole } from '@synergy/models';
import { getDeliveryStatusTranslationId } from '@synergy/models';
import { getLocalizedString } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useSearchParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { AccessControl } from '../../../common/components/access-control/access-control';
import { CustomerContactFilter } from '../../../common/components/filter/customer-contact-filter';
import { MEMBER_LIST_LIMIT } from '../../../common/utils/member-order-params';
import { OrderedArticle, todaysUnitPrice } from '../models/ordered-article';
import { SupplierFilter } from '../../../common/components/filter/supplier-filter';
import { localesSelector } from '../../../common/selectors/locale-selectors';
import { useAddToShopCart } from '../../orders/palo/use-add-to-shop-cart';
import { useOwnContactFilterNames } from '../../../common/utils/use-own-contact-filter-names';
import { useSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const EMPTY: PaginatedResponse<OrderedArticle> = { items: [], total: 0, page: 1, limit: 50 };

const isBuyable = (article: OrderedArticle): boolean => article.isAvailableForPurchase;

/** Up or down against the last order, in per cent; nothing within a cent. */
const PriceChange = ({ then, now }: { then?: number; now?: number }): ReactElement | null => {
    if (undefined === then || undefined === now || 0 === then || Math.abs(now - then) < 0.005) {
        return null;
    }

    const up = now > then;

    return (
        <Box component="span" sx={{ ml: '4px', fontSize: '11px', color: up ? tokens.color.crit : tokens.color.ok }}>
            {up ? '▲' : '▼'}{' '}
            <FormattedNumber value={Math.abs(now - then) / then} style="percent" maximumFractionDigits={1} />
        </Box>
    );
};

const PaloBuyAgainFilter = (): ReactElement => (
    <PaloFilterModal>
        <DateRangeFilter untilField="createdAtUntil" fromField="createdAtFrom" />
        <SupplierFilter />
        <AccessControl allowedRoles={UserRole.ShopAdmin}>
            <CustomerContactFilter />
        </AccessControl>
    </PaloFilterModal>
);

/**
 * "Nochmals kaufen" in the member portal (#2280): what was ordered before, as a
 * list to search, with the last order beside each article and its quantity
 * ready to change, several at once into the webshop's cart. The shop showed
 * them as tiles by year, one at a time into the cart.
 */
export const PaloBuyAgain = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const locales = useSelector(localesSelector);
    const currency = useSelector(state => state.currency);
    const ownContactNames = useOwnContactFilterNames();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params: sortedParams } = usePaloListSort('buy-again');
    const [articles, setArticles] = useState<PaginatedResponse<OrderedArticle>>(EMPTY);
    const [requesting, setRequesting] = useState(true);
    const [quantities, setQuantities] = useState<Record<number, number>>({});
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const { adding, add } = useAddToShopCart();

    const page = Number(searchParams.get('page') ?? 1);
    const limit = Number(searchParams.get('limit') ?? MEMBER_LIST_LIMIT);

    useEffect(() => {
        let cancelled = false;
        setRequesting(true);

        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.buyAgain.cget(sortedParams);

            if (!cancelled) {
                setArticles(result.success ? result.result : EMPTY);
                setRequesting(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [dispatch, sortedParams]);

    const quantityOf = (article: OrderedArticle): number =>
        quantities[article.product.id] ?? article.lastQuantity ?? article.product.minimumOrderQuantity ?? 1;

    const toCart = (picked: OrderedArticle[]): void => {
        void add(
            picked.map(article => ({
                productId: article.product.id,
                quantity: quantityOf(article),
                isAvailableForPurchase: isBuyable(article),
            }))
        ).then(() => setSelectedIds([]));
    };

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const pageIds = useMemo(() => articles.items.map(article => article.product.id), [articles.items]);
    const picked = articles.items.filter(article => selectedIds.includes(article.product.id));
    const pickedSum = picked.reduce(
        (sum, article) => sum + (todaysUnitPrice(article, currency) ?? 0) * quantityOf(article),
        0
    );

    const columns: PaloColumn<OrderedArticle>[] = [
        {
            key: 'name',
            type: 'name',
            head: <FormattedMessage id="app.orders.position.article" />,
            cell: article => (
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span>{getLocalizedString(locales, article.product.name)}</span>
                    <Box component="span" sx={{ fontSize: '11px', color: tokens.color.ink3 }}>
                        {[article.product.number, article.product.globalTradeItemNumber].filter(Boolean).join(' · ')}
                    </Box>
                </Box>
            ),
        },
        {
            key: 'supplier',
            type: 'classification',
            head: <FormattedMessage id="app.table.column.supplier" />,
            cell: article => <ValueOrNDash value={article.product.supplier?.name} />,
        },
        {
            key: 'orderCount',
            type: 'count',
            head: <FormattedMessage id="app.buyAgain.column.orderCount" />,
            cell: article => `${article.orderCount}×`,
        },
        {
            key: 'lastOrder',
            type: 'classification',
            head: <FormattedMessage id="app.buyAgain.column.lastOrder" />,
            variant: 'muted',
            cell: article => (
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span>
                        {article.lastOrderDate ? (
                            <FormattedDate value={article.lastOrderDate} year="numeric" month="short" day="2-digit" />
                        ) : (
                            '–'
                        )}
                    </span>
                    <Box component="span" sx={{ fontSize: '11px' }}>
                        {[article.lastOrderer, article.lastDeliveryCity].filter(Boolean).join(' · ')}
                    </Box>
                </Box>
            ),
        },
        {
            key: 'lastPrice',
            type: 'price',
            currency,
            head: <FormattedMessage id="app.buyAgain.column.priceThen" />,
            cell: article =>
                undefined !== article.lastNetPrice ? (
                    <PaloAmount value={article.lastNetPrice} maximumFractionDigits={4} />
                ) : (
                    '–'
                ),
        },
        {
            key: 'price',
            type: 'price',
            currency,
            head: <FormattedMessage id="app.buyAgain.column.priceNow" />,
            cell: article => {
                const now = todaysUnitPrice(article, currency);

                return undefined !== now ? (
                    <>
                        <PaloAmount value={now} maximumFractionDigits={4} />
                        <PriceChange then={article.lastNetPrice} now={now} />
                    </>
                ) : (
                    '–'
                );
            },
        },
        {
            key: 'delivery',
            type: 'classification',
            head: <FormattedMessage id="app.buyAgain.column.delivery" />,
            variant: 'muted',
            cell: article =>
                !isBuyable(article) ? (
                    <FormattedMessage id="app.buyAgain.notBuyable" />
                ) : article.product.deliveryStatus ? (
                    <FormattedMessage
                        id={getDeliveryStatusTranslationId(article.product.deliveryStatus)}
                        values={{ days: article.product.deliveryTimeInDays ?? 0 }}
                    />
                ) : (
                    '–'
                ),
        },
        {
            key: 'quantity',
            type: 'quantity',
            head: <FormattedMessage id="app.orders.position.quantity" />,
            cell: article => (
                <Box
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    onClick={e => e.stopPropagation()}
                >
                    <TextField
                        size="small"
                        type="number"
                        value={quantityOf(article)}
                        inputProps={{
                            min: article.product.minimumOrderQuantity ?? 1,
                            style: { width: 64, textAlign: 'right' },
                        }}
                        onChange={event =>
                            setQuantities(current => ({
                                ...current,
                                [article.product.id]: Math.max(0, Number(event.target.value)),
                            }))
                        }
                    />
                    <span>{getLocalizedString(locales, article.product.orderUnit)}</span>
                </Box>
            ),
        },
    ];

    return (
        <UrlFilter>
            <PaloListView<OrderedArticle, number>
                title={<FormattedMessage id="app.navigation.buyAgain" />}
                filters={<PaloBuyAgainFilter />}
                filterChips={<PaloFilterChips names={ownContactNames} />}
                sort={sort}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder={intl.formatMessage({ id: 'app.buyAgain.searchPlaceholder' })}
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                columns={columns}
                items={articles.items}
                getRowId={article => article.product.id}
                requesting={requesting}
                onRowClick={article => window.location.assign(urlJoin(SHOP_URL, 'product', String(article.product.id)))}
                isRowMuted={article => !isBuyable(article)}
                selection={{
                    selectedIds,
                    allSelected: pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id)),
                    someSelected: pageIds.some(id => selectedIds.includes(id)),
                    onToggle: id =>
                        setSelectedIds(current =>
                            current.includes(id) ? current.filter(other => other !== id) : [...current, id]
                        ),
                    onToggleAll: () =>
                        setSelectedIds(current => (pageIds.every(id => current.includes(id)) ? [] : pageIds)),
                    disabledIds: articles.items
                        .filter(article => !isBuyable(article))
                        .map(article => article.product.id),
                    disabledHint: <FormattedMessage id="app.buyAgain.notBuyable" />,
                }}
                selectionActions={
                    picked.length > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <FormattedMessage
                                id="app.buyAgain.selected"
                                values={{
                                    count: picked.length,
                                    sum: <FormattedNumber value={pickedSum} style="currency" currency={currency} />,
                                }}
                            />
                            <Button size="small" variant="contained" disabled={adding} onClick={() => toCart(picked)}>
                                <FormattedMessage id="app.buyAgain.addSelected" values={{ count: picked.length }} />
                            </Button>
                        </Box>
                    ) : undefined
                }
                rowActions={article => [
                    {
                        label: <FormattedMessage id="app.orders.addToCart" />,
                        onClick: () => toCart([article]),
                        disabled: !isBuyable(article),
                    },
                ]}
                emptyLabel={<FormattedMessage id="app.buyAgain.empty" />}
                pagination={{
                    count: articles.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            />
        </UrlFilter>
    );
};
